/**
 * This is a NavigationRailHorizontal component. This component displays the NavigationRail as a carousel rather than as a group.
 *
 * @module views/components/NavigationRailHorizontal
 * @memberof -Common
 */
import './NavigationRailHorizontal.scss';

import React, { useRef, useState } from 'react';

import PropTypes from 'prop-types';

import Rail, { Slide } from '@ulta/core/components/Rail/Rail';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';

import { useUuid } from '@ulta/hooks/useUuid/useUuid';

import carouselUtils from '@ulta/utils/carouselUtils/carouselUtils';

import NavigationCard from '../NavigationCard/NavigationCard';

/**
 * Represents a NavigationRailHorizontal component
 *
 * @method
 * @param {NavigationRailHorizontalProps} props - React properties passed from composition
 * @returns NavigationRailHorizontal
 */
export const NavigationRailHorizontal = function( props ){
  const navigationRailRef = useRef( null );

  const { breakpoint } = useDeviceInflection();

  const {
    backgroundColor,
    dataCapture,
    items,
    outlineOption,
    shopAllLabel,
    shopAllAction,

    /* Rail Title */
    title,

    /* Rail meta data */
    carouselAccessibility,
    carouselCardAccessibility,
    carouselDetailsAccessibility,
    nextClickAction,
    previousClickAction,
    previousAccessibility,
    nextAccessibility
  } = props;

  /* Setting variables */
  const isXLargeDevice = breakpoint.CURRENT_BREAKPOINT === 'XL';
  const withShopAll = shopAllAction && shopAllLabel;
  const spaceBetween = isXLargeDevice ? 16 + 1 : 8 + 1; // Adding 1px because Swiper doesn't account for border widths correctly

  /* Sets the UUID for the rail buttons */
  const [railUuid] = useUuid();

  // State to store the Swiper instance from the child Rail component.
  const [swiperInstance, setSwiperInstance] = useState( null );

  // Callback to be passed to the Rail component.
  // This will be called from the child when Swiper's onSwiper event fires.
  const handleSwiperLoaded = ( swiper ) => setSwiperInstance( swiper );

  /* Interpolating accesibility strings */
  const newCarouselAccessibility = carouselUtils.handleTokenizedString( carouselAccessibility, [title] );
  const newCarouselCardAccessibility = carouselUtils.handleTokenizedString( carouselCardAccessibility, [title] );
  const newCarouselDetailsAccessibility = carouselUtils.handleTokenizedString( carouselDetailsAccessibility, [title] );

  /* ADA Objects */
  const _railADAmeta = {
    carouselAccessibility: newCarouselAccessibility,
    carouselCardAccessibility: newCarouselCardAccessibility,
    carouselDetailsAccessibility: newCarouselDetailsAccessibility
  };

  const _railMeta = {
    nextAccessibility: nextAccessibility,
    previousAccessibility: previousAccessibility
  };

  return (
    <div
      className='NavigationRailHorizontal'
      ref={ navigationRailRef }
    >
      <Rail
        railRef={ navigationRailRef }
        id={ railUuid }
        items={ items }
        _railADAmeta={ _railADAmeta }
        _railMeta={ _railMeta }
        spaceBetween={ spaceBetween }
        onSwiperLoaded={ handleSwiperLoaded }
        nextClickAction={ nextClickAction }
        previousClickAction={ previousClickAction }
        dataCapture={ dataCapture }
        sliderPerViewXL={ 9 }
        slidesPerGroupXL={ 9 }
        sliderPerViewLG={ 9 }
        slidesPerGroupLG={ 9 }
        sliderPerViewMD={ 8 }
        slidesPerGroupMD={ 8 }
        sliderPerViewSM={ 3.4 }
        slidesPerGroupSM={ 3 }
      >
        <>
          { withShopAll && (
            <Slide>
              <NavigationCard
                shopAllLabel={ shopAllLabel }
                shopAllAction={ shopAllAction }
                outlineOption={ outlineOption }
                backgroundColor={ backgroundColor }
              />
            </Slide>
          ) }
          { items?.map( ( item, index ) => (
            <Slide key={ index }>
              <NavigationCard { ...item } outlineOption={ outlineOption } backgroundColor={ backgroundColor } />
            </Slide>
          ) ) }
        </>
      </Rail>
    </div>
  );
};

/**
 * Property type definitions
 * @typedef NavigationRailHorizontalProps
 * @type {object}
 * @property {array} items - Sets the items to be displayed as NavigationCards for the rail.
 * @property {string} shopAllLabel - Sets the all shop label.
 * @property {object} shopAllAction - Sets the shop all action.
 * @property {object} outlineOption - True if the Navigation Cards should have an outline; otherwise, false.
 * @property {string} previousAccessibility - Sets the aria label for previous button.
 * @property {string} nextAccessibility - Sets the aria label for next button.
 * @property {string} nextClickAction - Sets the action for previous button.
 * @property {string} previousClickAction - Sets the action for next button.
 * @property {string} carouselAccessibility - Value for aria label for carousel
 * @property {string} carouselDetailsAccessibility - Value for aria label for carousel
 * @property {string} carouselAccessibility - Value for aria label for product rail
 * @property {string} title - Sets the title for CompactDealRail list.
 * @property {string} backgroundColor - Sets the background color for the navigation card.
 */
export const propTypes = {
  items: PropTypes.array,
  shopAllLabel: PropTypes.string,
  shopAllAction: PropTypes.object,
  previousAccessibility: PropTypes.string,
  nextAccessibility: PropTypes.string,
  nextClickAction: PropTypes.object,
  previousClickAction: PropTypes.object,
  carouselAccessibility: PropTypes.string,
  carouselDetailsAccessibility: PropTypes.string,
  outlineOption: PropTypes.bool,
  title: PropTypes.string,
  backgroundColor: PropTypes.string
};

/**
 * Default values for passed properties
 * @type {object}
 */

export const defaultProps = {
  carouselAccessibility: '',
  railAccessibility: ''
};

NavigationRailHorizontal.propTypes = propTypes;
NavigationRailHorizontal.defaultProps = defaultProps;
export default NavigationRailHorizontal;
